import * as React from "react"
import * as SliderPrimitive from "@radix-ui/react-slider"

import { cn } from "@/lib/utils"

function Slider({
  className,
  defaultValue,
  value,
  min = 0,
  max = 100,
  step,
  onValueChange,
  ...props
}: React.ComponentProps<typeof SliderPrimitive.Root>) {
  const _values = React.useMemo(
    () =>
      Array.isArray(value)
        ? value
        : Array.isArray(defaultValue)
          ? defaultValue
          : [min, max],
    [value, defaultValue, min, max]
  )

  const [currentValues, setCurrentValues] = React.useState(_values)

  // Keep in sync when controlled
  React.useEffect(() => {
    if (value !== undefined) setCurrentValues(value as number[])
  }, [value])

  const handleValueChange = React.useCallback(
    (vals: number[]) => {
      setCurrentValues(vals)
      onValueChange?.(vals)
    },
    [onValueChange]
  )

  const isTickActive = (tick: number) => {
    if (currentValues.length === 1) return tick <= currentValues[0]
    return tick >= currentValues[0] && tick <= currentValues[currentValues.length - 1]
  }

  const ticks = React.useMemo(() => {
    if (!step || step <= 0 || !Number.isFinite(step) || min > max) return []
    const result = []
    for (let v = min; v <= max; v += step) {
      result.push(Math.round(v * 1e10) / 1e10)
    }
    if (result[result.length - 1] !== max) result.push(max)
    return result
  }, [min, max, step])

  return (
    <SliderPrimitive.Root
      data-slot="slider"
      defaultValue={defaultValue}
      value={value}
      min={min}
      max={max}
      step={step}
      onValueChange={handleValueChange}
      className={cn(
        "relative flex w-full touch-none items-center select-none data-disabled:opacity-50 data-[orientation=vertical]:h-full data-[orientation=vertical]:min-h-44 data-[orientation=vertical]:w-auto data-[orientation=vertical]:flex-col",
        className
      )}
      {...props}
    >
      <SliderPrimitive.Track
        data-slot="slider-track"
        className={cn(
          "bg-muted relative grow overflow-hidden rounded-pill data-[orientation=horizontal]:h-1.75 data-[orientation=horizontal]:w-full data-[orientation=vertical]:h-full data-[orientation=vertical]:w-1.5"
        )}
      >
        <SliderPrimitive.Range
          data-slot="slider-range"
          className={cn(
            "bg-primary absolute data-[orientation=horizontal]:h-full data-[orientation=vertical]:w-full"
          )}
        />
      </SliderPrimitive.Track>

      {ticks.length > 0 && (
        <div className="absolute inset-x-2 flex items-center pointer-events-none">
          {ticks.map((tick) => {
            const pct = ((tick - min) / (max - min)) * 100
            const active = isTickActive(tick)
            return (
              <div
                key={tick}
                className="absolute -translate-x-1/2"
                style={{ left: `${pct}%` }}
              >
                <div
                  className={cn(
                    "size-0.75 rounded-full",
                    active ? "bg-primary" : "bg-muted-foreground/30",
                  )}
                />
              </div>
            )
          })}
        </div>
      )}

      {Array.from({ length: _values.length }, (_, index) => (
        <SliderPrimitive.Thumb
          data-slot="slider-thumb"
          key={index}
          className="active:border-primary border-muted-foreground/30 focus-visible:border-primary ring-ring/20 hover:cursor-grab data-disabled:hover:cursor-default active:cursor-grabbing data-disabled:active:cursor-default block h-5 w-2.5 shrink-0 rounded-pill border bg-white shadow-sm transition-[color,box-shadow] hover:ring-5 focus-visible:ring-5 focus-visible:outline-hidden disabled:pointer-events-none disabled:opacity-50 relative after:absolute after:left-1/2 after:-translate-x-1/2 after:top-1/2 after:-translate-y-1/2 after:h-2 after:w-px after:bg-muted-foreground/40 active:after:bg-primary after:rounded-pill"
        />
      ))}
    </SliderPrimitive.Root>
  )
}

export { Slider }